// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex } from '@locus-taxy/neo-ui';

import { isEnumControl, or, rankWith, uiTypeIs } from '@jsonforms/core';

import ArrayObjectModal from './ArrayObjectModal';
import useControlProps from '../../util/useControlProps';

type Props = {};

const ArrayObjectControl = (props: Props): React.Node => {
    const [showModal, setShowModal] = React.useState(false);

    const {
        t,
        i18n: { language }
    } = useTranslation('platform');

    const {
        data: objectArray,
        handleChange,
        label,
        path,
        schema,
        uischema: { options },
        visible,
        context
    } = useControlProps(props);

    const { edit, view, canDelete } = options;
    const metadata = { edit, view };
    const { product } = context;

    const i18n = React.useMemo(() => {
        return {
            locale: language,
            translate: (key, origKey) => {
                const _parentPath = path.replaceAll('.', '.fields.');
                const _key = `settings.${product}.fields.${_parentPath}.${key}`;

                // for now only translate if the key is `label` or `description`
                if (key.endsWith('label') || key.endsWith('description')) {
                    return t(_key);
                }
                return origKey;
            }
        };
    }, [language, path, product, t]);

    const labels = React.useMemo(() => {
        const fieldPath = path.replace('.', '.fields.');
        const basePath = `settings.${product}.fields.${fieldPath}`;

        return {
            headerText: t(`${basePath}.headerText`),
            actionText: t(`${basePath}.actionText`),
            drawer: {
                headerText: t(`${basePath}.editView.headerText`),
                saveActionText: t(`${basePath}.editView.saveActionText`),
                dismissActionText: t(`${basePath}.editView.dismissActionText`)
            }
        };
    }, [path, product, t]);

    const toggleShowModal = () => {
        setShowModal(show => !show);
    };

    const handleSave = data => {
        handleChange(path, data ? data : schema.default ?? []);
    };

    return (
        visible && (
            <>
                <Flex justifyContent="flex-end">
                    <Button palette="primary" onClick={toggleShowModal}>
                        {label}
                    </Button>
                </Flex>

                {showModal && (
                    <ArrayObjectModal
                        context={context}
                        canDelete={canDelete}
                        i18n={i18n}
                        labels={labels}
                        metadata={metadata}
                        objectArray={objectArray}
                        onDismiss={toggleShowModal}
                        onSave={handleSave}
                    />
                )}
            </>
        )
    );
};

export default ArrayObjectControl;

export const arrayObjectControlTester = rankWith(
    1,
    or(uiTypeIs('ArrayObjectControl'), isEnumControl)
);
